import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Check, X, Download, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const apply = (text, changes, accepted) => changes.reduce((t, c, i) => (accepted[i] ? t.replace(c.original, () => c.corregido) : t), text);

// Corrected brief: each change marked (accept/reject) or the full AI version; editable and downloadable as Word
export const RewriteResult = ({ data, title }) => {
  const { api } = useAuth();
  const [accepted, setAccepted] = useState(() => data.changes.map(() => true));
  const [mode, setMode] = useState(data.changes.length ? 'marked' : 'full');
  const [edited, setEdited] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = useMemo(() => (mode === 'full' ? data.corrected : apply(data.original, data.changes, accepted)), [mode, accepted, data]);
  const text = edited ?? base;
  const keepEdits = () => edited === null || window.confirm('Perderás tus ediciones manuales. ¿Continuar?');
  const toggle = (i) => { if (!keepEdits()) return; setEdited(null); setAccepted((a) => a.map((v, j) => (j === i ? !v : v))); };
  const switchMode = (m) => { if (m === mode || !keepEdits()) return; setMode(m); setEdited(null); };
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.post('/cerebro/docx', { text, title: `${title} - corregido` }, { responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = `${title.slice(0, 50)} - corregido.docx`; a.click();
    } catch { toast.error('No se pudo generar el Word'); } finally { setBusy(false); }
  };
  const btn = (m) => `rounded-full px-3 py-1 text-xs ${mode === m ? 'bg-amber-500 text-slate-950' : 'border border-slate-600 text-slate-300'}`;
  return (
    <div className="space-y-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="rewrite-result">
      <div className="flex flex-wrap gap-2">
        <button data-testid="rewrite-mode-marked" onClick={() => switchMode('marked')} className={btn('marked')}>Cambios marcados ({data.changes.length})</button>
        <button data-testid="rewrite-mode-full" onClick={() => switchMode('full')} className={btn('full')}>Versión completa corregida</button>
      </div>
      {mode === 'marked' && data.changes.map((c, i) => (
        <div key={i} data-testid={`rewrite-change-${i}`} className={`rounded-lg border p-2 text-xs ${accepted[i] ? 'border-emerald-500/40' : 'border-slate-700 opacity-60'}`}>
          <p className="text-red-300 line-through">{c.original}</p>
          <p className="text-emerald-300 mt-1">{c.corregido}</p>
          <div className="mt-1 flex items-center justify-between gap-2">
            <span className="text-slate-400">{c.motivo}</span>
            <button data-testid={`rewrite-toggle-${i}`} onClick={() => toggle(i)} className="flex items-center gap-1 rounded-full border border-slate-600 px-2 py-0.5 text-[11px] text-slate-200 shrink-0">
              {accepted[i] ? <><X className="h-3 w-3" />Rechazar</> : <><Check className="h-3 w-3" />Aceptar</>}
            </button>
          </div>
        </div>
      ))}
      {mode === 'marked' && data.additions?.length > 0 && <p className="text-[11px] text-amber-200">Además la IA propone {data.additions.length} párrafo(s) nuevo(s); están incluidos en la «Versión completa corregida».</p>}
      <textarea data-testid="rewrite-final-text" value={text} onChange={(e) => setEdited(e.target.value)} rows={12} className="w-full rounded-lg border border-slate-700 bg-slate-950 p-3 text-xs text-slate-100 font-mono" />
      <p className="text-[11px] text-slate-500">Revisa los marcadores [COMPLETAR: ...] antes de presentar.</p>
      <button data-testid="rewrite-download-docx" onClick={download} disabled={busy} className="w-full rounded-lg bg-emerald-500 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-60 flex items-center justify-center gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Descargar en Word
      </button>
    </div>
  );
};
